import { RuleDmpDto, UserDTO } from '@tj/tj-dmp-lib-api';

/*
 * Admin levels (DMP2-736). Three levels share the ROLE_ADMIN role:
 * - super-admin: Keycloak group Admins, `account.admin`;
 * - admin tenant: ROLE_ADMIN on a group with the partner ALL (`account.adminForTenant`);
 * - admin affilié: ROLE_ADMIN on a group with a given partner, read from `account.rules`.
 * The gateway decides; these helpers only let the screens show what it allows.
 */

export const ROLE_ADMIN = 'ROLE_ADMIN';

// Permission token of PagePermissionsService and of the menu: any admin level, on any group.
export const ANY_ADMIN = 'ANY_ADMIN';

const ALL = 'ALL';
const NONE = 'NONE';

export function isSuperAdmin(account: UserDTO | null | undefined): boolean {
    return account?.admin === true;
}

// `adminForTenant` is computed by the API from the ROLE_ADMIN entries with the partner ALL; it is empty for a
// super-admin.
export function isTenantAdmin(account: UserDTO | null | undefined, tenant: string | null | undefined): boolean {
    const adminForTenant = account?.adminForTenant as unknown as Record<string, boolean> | undefined;
    return !!(tenant && adminForTenant?.[tenant]);
}

// What the screens call an admin of the group: the super-admin, or an admin tenant of that group.
export function isAdminOfTenant(account: UserDTO | null | undefined, tenant: string | null | undefined): boolean {
    return isSuperAdmin(account) || isTenantAdmin(account, tenant);
}

export function isAffiliateAdmin(account: UserDTO | null | undefined, tenant: string, partner: string): boolean {
    return (account?.rules ?? []).some(entry =>
        isAdminEntry(entry) && entry.tenant === tenant && isPartner(entry.partner) && entry.partner === partner);
}

/**
 * Rights an admin may grant in the user screens (DMP2-851): mirror of the gateway's UserManagementScope (DMP2-845),
 * which stays the reference. The screens only hide what the gateway would refuse.
 * - super-admin: everything;
 * - admin tenant of T: any right on T, ROLE_ADMIN only with a given partner (an admin affilié);
 * - admin affilié of (T, X): rights on T for the partner X only, never ROLE_ADMIN.
 */
export class UserManagementScope {

    private constructor(
        private readonly superAdmin: boolean,
        // Groups the account is admin tenant of.
        private readonly adminTenants: Set<string>,
        // Partners the account is admin affilié of, by group.
        private readonly adminAffiliates: Map<string, Set<string>>
    ) {
    }

    // `account.rules` holds the entries of the account, a role being expanded into its rules with the role kept.
    static of(account: UserDTO | null | undefined): UserManagementScope {
        const adminTenants = new Set<string>();
        const adminAffiliates = new Map<string, Set<string>>();
        for (const entry of account?.rules ?? []) {
            if (!isAdminEntry(entry) || !isTenant(entry.tenant)) {
                continue;
            }
            if (isAll(entry.partner)) {
                adminTenants.add(entry.tenant);
            } else if (isPartner(entry.partner)) {
                adminAffiliates.set(entry.tenant, (adminAffiliates.get(entry.tenant) ?? new Set<string>()).add(entry.partner));
            }
        }
        return new UserManagementScope(isSuperAdmin(account), adminTenants, adminAffiliates);
    }

    isSuperAdmin(): boolean {
        return this.superAdmin;
    }

    isAdmin(): boolean {
        return this.superAdmin || this.adminTenants.size > 0 || this.adminAffiliates.size > 0;
    }

    // Groups on which the account may grant rights.
    coversTenant(tenant: string): boolean {
        return this.superAdmin || this.adminTenants.has(tenant) || this.adminAffiliates.has(tenant);
    }

    // May the account grant this entry (a rule, or a role on a group and a partner)?
    covers(entry: RuleDmpDto): boolean {
        if (this.superAdmin) {
            return true;
        }
        if (!isTenant(entry.tenant)) {
            return false;
        }
        // The admin tenant manages the admins affiliés and the users of his group, not the other admins tenant.
        if (this.adminTenants.has(entry.tenant)) {
            return !isAdminEntry(entry) || isPartner(entry.partner);
        }
        // The admin affilié manages the users of his partner only.
        return !isAdminEntry(entry) && (this.adminAffiliates.get(entry.tenant)?.has(entry.partner) ?? false);
    }
}

// Any admin level, on any group: may open the user screens.
export function isAnyAdmin(account: UserDTO | null | undefined): boolean {
    return UserManagementScope.of(account).isAdmin();
}

function isAdminEntry(entry: RuleDmpDto): boolean {
    return entry.role === ROLE_ADMIN;
}

// A given group: neither empty nor ALL.
function isTenant(tenant: string | null | undefined): boolean {
    return !!tenant?.trim() && !isAll(tenant);
}

// A given partner: neither empty, nor ALL, nor NONE.
function isPartner(partner: string | null | undefined): boolean {
    return !!partner?.trim() && !isAll(partner) && partner.toUpperCase() !== NONE;
}

function isAll(code: string | null | undefined): boolean {
    return code?.toUpperCase() === ALL;
}
